import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { HandCoins, Search, CheckCircle2, XCircle, Loader2, Clock, AlertTriangle, CalendarDays, Moon, Hotel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { escrowApi, type PendingEscrowResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import { resolveMediaUrl } from "@/lib/api";

export const Route = createFileRoute("/admin/escrows")({
  component: EscrowsPage,
});

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

function EscrowsPage() {
  const [escrows, setEscrows] = useState<PendingEscrowResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  useEffect(() => {
    fetchEscrows();
  }, []);

  const fetchEscrows = async () => {
    setLoading(true);
    try {
      const data = await escrowApi.getPending();
      setEscrows(data);
    } catch {
      toast.error("Failed to load pending escrows");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    setProcessingId(id);
    try {
      await escrowApi.approve(id);
      toast.success("Payment approved and released to your wallet");
      fetchEscrows();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Approval failed";
      toast.error(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: string) => {
    setProcessingId(id);
    try {
      await escrowApi.reject(id, rejectReason || undefined);
      toast.success("Payment rejected and refunded to customer");
      setRejectingId(null);
      setRejectReason("");
      fetchEscrows();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Rejection failed";
      toast.error(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const filtered = escrows.filter((e) => {
    const s = search.toLowerCase();
    return (
      !s ||
      e.customerName.toLowerCase().includes(s) ||
      (e.itemName?.toLowerCase() ?? "").includes(s) ||
      e.customerEmail.toLowerCase().includes(s)
    );
  });

  const urgent = filtered.filter((e) => e.daysPending >= 4);
  const normal = filtered.filter((e) => e.daysPending < 4);

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Pending Payments</h1>
          <p className="text-sm text-muted-foreground">Approve or reject customer payments for your venues and events.</p>
        </div>
        <Badge variant="secondary" className="gap-1.5">
          <HandCoins className="size-3" /> {escrows.length} pending
        </Badge>
      </div>

      {urgent.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-center gap-2">
          <AlertTriangle className="size-4" />
          <span><strong>{urgent.length}</strong> payment(s) are nearing auto-rejection (24 hours). Approve or reject them soon.</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search pending payments..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-64" />
        </div>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card py-20 text-center shadow-soft">
          <HandCoins className="mb-3 size-12 text-muted-foreground/30" />
          <p className="font-semibold text-muted-foreground">No pending payments</p>
          <p className="text-sm text-muted-foreground mt-1">Customer payments will appear here for approval.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((e) => {
            const isUrgent = e.daysPending >= 4;
            const isRejecting = rejectingId === e.escrowId;
            const isVenue = e.context === "venue_booking" || e.context === "venue_extension";
            const isExtension = e.context === "venue_extension";
            return (
              <div key={e.escrowId} className={`rounded-2xl border bg-card p-5 shadow-soft ${isUrgent ? "border-amber-300" : "border-border/60"}`}>
                <div className="flex flex-col gap-4 sm:flex-row">
                  {/* Venue Image */}
                  {isVenue && e.venueImage && (
                    <div className="relative h-32 w-full sm:w-44 shrink-0 overflow-hidden rounded-xl">
                      <img
                        src={resolveMediaUrl(e.venueImage)}
                        alt={e.itemName ?? "Venue"}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  {isVenue && !e.venueImage && (
                    <div className="relative h-32 w-full sm:w-44 shrink-0 overflow-hidden rounded-xl bg-accent/40 flex items-center justify-center">
                      <Hotel className="size-10 text-muted-foreground/30" />
                    </div>
                  )}

                  <div className="flex-1 space-y-2">
                    {/* Header */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{e.itemName ?? "Booking/Event"}</h3>
                      {isUrgent && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1">
                          <Clock className="size-3" /> {5 - e.daysPending} day(s) left
                        </Badge>
                      )}
                    </div>

                    {/* Customer */}
                    <p className="text-sm text-muted-foreground">{e.customerName} — {e.customerEmail}</p>

                    {/* Booking Details */}
                    {isVenue && (e.startDate || e.endDate) && (
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1 rounded-md bg-accent/40 px-2 py-0.5">
                          <CalendarDays className="size-3" />
                          {e.startDate ? fmtDate(e.startDate) : "N/A"} — {e.endDate ? fmtDate(e.endDate) : "N/A"}
                        </span>
                        {e.daysCount && (
                          <span className="flex items-center gap-1 rounded-md bg-accent/40 px-2 py-0.5">
                            <Moon className="size-3" />
                            {e.daysCount} night{e.daysCount !== 1 ? "s" : ""}
                          </span>
                        )}
                        {e.roomType && (
                          <span className="rounded-md bg-accent/40 px-2 py-0.5">{e.roomType}</span>
                        )}
                      </div>
                    )}

                    {/* Tags */}
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span className="rounded-md bg-accent/40 px-2 py-0.5">{isExtension ? "Venue Extension" : isVenue ? "Venue Booking" : "Event Ticket"}</span>
                      <span className="rounded-md bg-accent/40 px-2 py-0.5">Created {fmtDate(e.createdAt)}</span>
                      <span className="rounded-md bg-accent/40 px-2 py-0.5">{e.daysPending} day(s) pending</span>
                    </div>

                    {/* Total Amount */}
                    {e.totalAmount && (
                      <p className="text-xs text-muted-foreground">
                        Booking total: <span className="font-semibold text-foreground">{formatNaira(e.totalAmount)}</span>
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-end gap-3 sm:justify-center">
                    <div className="text-right">
                      <p className="font-display text-xl font-bold">{formatNaira(e.netAmount)}</p>
                      <p className="text-[10px] text-muted-foreground">You receive ({Math.round(e.commissionRate * 100)}% after service charge)</p>
                    </div>
                    {processingId === e.escrowId ? (
                      <Loader2 className="size-5 animate-spin text-muted-foreground" />
                    ) : (
                      <div className="flex gap-2">
                        {!isRejecting ? (
                          <>
                            <Button size="sm" variant="outline" className="h-9 gap-1 text-emerald-600" onClick={() => handleApprove(e.escrowId)}>
                              <CheckCircle2 className="size-3.5" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="h-9 gap-1 text-red-600" onClick={() => { setRejectingId(e.escrowId); setRejectReason(""); }}>
                              <XCircle className="size-3.5" /> Reject
                            </Button>
                          </>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Input placeholder="Reason (optional)" value={rejectReason} onChange={(ev) => setRejectReason(ev.target.value)} className="h-9 w-48 text-xs" />
                            <Button size="sm" variant="destructive" className="h-9" onClick={() => handleReject(e.escrowId)}>Reject</Button>
                            <Button size="sm" variant="ghost" className="h-9" onClick={() => setRejectingId(null)}>Cancel</Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
